import { Button, FormField, Input, Multiselect, Select, SelectProps, SpaceBetween, Textarea, Toggle } from '@amzn/awsui-components-core';
import { FieldArray, FormikProps, getIn } from 'formik';
import React from 'react';
import { ApplicationFormValues } from '../../AdminModels';
import MultiEmployeeLookup from 'src/components/generic-components/employee-lookup/MultiEmployeeLookup';
import { SelectedEmployee } from 'src/components/generic-components/employee-lookup/SingleEmployeeLookup';

const OS_OPTIONS: SelectProps.Option[] = [
  { value: 'macOS', label: 'macOS' },
  { value: 'Windows', label: 'Windows' },
  { value: 'Linux', label: 'Linux' }
];

const RESOURCE_ICON_OPTIONS: SelectProps.Option[] = [
  { value: 'chat', label: 'Chat (Slack)' },
  { value: 'document', label: 'Document (Wiki/Docs)' },
  { value: 'globe', label: 'Globe (External Link)' },
  { value: 'video', label: 'Video' },
  { value: 'code', label: 'Code' },
  { value: 'settings', label: 'Settings' }
];

const MCP_CATEGORY_OPTIONS: SelectProps.Option[] = [
  { value: 'DaS Finance', label: 'DaS Finance' },
  { value: 'General', label: 'General' }
];

const FEATURE_ICON_OPTIONS: SelectProps.Option[] = [
  { value: 'database', label: 'Database' },
  { value: 'file-text', label: 'File/Report' },
  { value: 'download', label: 'Download/Export' },
  { value: 'zap', label: 'Automate/Lightning' },
  { value: 'search', label: 'Search/Discover' },
  { value: 'bar-chart-2', label: 'Chart/Analytics' },
  { value: 'shield', label: 'Security/Shield' },
  { value: 'refresh-cw', label: 'Sync/Refresh' },
  { value: 'layers', label: 'Layers/Stack' },
  { value: 'git-branch', label: 'Branch/Flow' },
  { value: 'terminal', label: 'Terminal' },
  { value: 'code', label: 'Code' },
  { value: 'globe', label: 'Globe/Web' },
  { value: 'message-square', label: 'Chat/Message' },
  { value: 'settings', label: 'Settings/Gear' },
  { value: 'cpu', label: 'CPU/Compute' },
  { value: 'table', label: 'Table/Grid' },
  { value: 'upload', label: 'Upload' }
];

interface McpConfigProps {
  formik: FormikProps<ApplicationFormValues>;
}

const McpConfigSection: React.FC<McpConfigProps> = ({ formik }) => {
  const isEnabled = formik.values.applicationDetails?.mcpConfig?.isEnabled || false;

  // Map contacts to SelectedEmployee format for MultiEmployeeLookup
  const contacts = formik.values.applicationDetails?.mcpConfig?.contacts || [];
  const contactEmployees: SelectedEmployee[] = contacts.map((c: { alias: string; role: string }) => ({
    login: c.alias,
    fullName: c.alias
  }));

  const handleContactsChange = (employees: SelectedEmployee[]) => {
    // Preserve existing roles when list changes
    const updatedContacts = employees.map((emp) => {
      const existing = contacts.find((c: { alias: string }) => c.alias === emp.login);
      return { alias: emp.login, role: existing?.role || '' };
    });
    formik.setFieldValue('applicationDetails.mcpConfig.contacts', updatedContacts);
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <SpaceBetween size="l">
      {/* Basic Info */}
      <SpaceBetween size="m">
        <FormField
          label="MCP Description"
          description="Short tagline shown on the MCP server card and detail page header. Explain what users can do with this MCP."
        >
          <Textarea
            value={formik.values.applicationDetails?.mcpConfig?.description || ''}
            onChange={({ detail }) => formik.setFieldValue('applicationDetails.mcpConfig.description', detail.value)}
            placeholder="Query financial cubes, run reports, and export data — all from your AI assistant."
            rows={2}
          />
        </FormField>

        <FormField label="Category" description="Groups this server in the MCP page sidebar. Servers with the same category appear together.">
          <Select
            selectedOption={
              formik.values.applicationDetails?.mcpConfig?.category
                ? MCP_CATEGORY_OPTIONS.find((o) => o.value === formik.values.applicationDetails?.mcpConfig?.category) || null
                : null
            }
            onChange={({ detail }) => formik.setFieldValue('applicationDetails.mcpConfig.category', detail.selectedOption.value)}
            options={MCP_CATEGORY_OPTIONS}
            placeholder="Select a category"
          />
        </FormField>

        <FormField label="Featured" description="Featured servers are highlighted at the top of the MCP listing page.">
          <Toggle
            checked={formik.values.applicationDetails?.mcpConfig?.isFeatured || false}
            onChange={({ detail }) => formik.setFieldValue('applicationDetails.mcpConfig.isFeatured', detail.checked)}
          >
            Show on featured list
          </Toggle>
        </FormField>
      </SpaceBetween>

      {/* Terminal Commands */}
      <FormField label="Terminal Commands" description="Installation commands grouped by OS and method.">
        <FieldArray name="applicationDetails.mcpConfig.terminalCommands">
          {({ push, remove }) => (
            <SpaceBetween size="m">
              {(formik.values.applicationDetails?.mcpConfig?.terminalCommands || []).map((_: unknown, index: number) => (
                <div key={index} style={{ padding: '12px', border: '1px solid var(--color-border-divider-default, #e9ebed)', borderRadius: 8 }}>
                  <SpaceBetween size="s">
                    <FormField label="OS">
                      <Multiselect
                        selectedOptions={(getIn(formik.values, `applicationDetails.mcpConfig.terminalCommands.${index}.os`) || []).map(
                          (osVal: string) => OS_OPTIONS.find((o) => o.value === osVal) || { value: osVal, label: osVal }
                        )}
                        onChange={({ detail }) =>
                          formik.setFieldValue(
                            `applicationDetails.mcpConfig.terminalCommands.${index}.os`,
                            detail.selectedOptions.map((opt) => opt.value)
                          )
                        }
                        options={OS_OPTIONS}
                        placeholder="OS(es)"
                        tokenLimit={3}
                      />
                    </FormField>
                    <SpaceBetween size="xs" direction="horizontal" alignItems="end">
                      <FormField label="Method">
                        <Input
                          value={getIn(formik.values, `applicationDetails.mcpConfig.terminalCommands.${index}.method`) || ''}
                          onChange={({ detail }) =>
                            formik.setFieldValue(`applicationDetails.mcpConfig.terminalCommands.${index}.method`, detail.value)
                          }
                          placeholder="pip, brew, npm"
                        />
                      </FormField>
                      <FormField label="Command" stretch>
                        <Input
                          value={getIn(formik.values, `applicationDetails.mcpConfig.terminalCommands.${index}.command`) || ''}
                          onChange={({ detail }) =>
                            formik.setFieldValue(`applicationDetails.mcpConfig.terminalCommands.${index}.command`, detail.value)
                          }
                          placeholder="pip install das-aim && das-aim init prophecy"
                        />
                      </FormField>
                      <Button formAction="none" iconName="remove" variant="icon" onClick={() => remove(index)} ariaLabel="Remove command" />
                    </SpaceBetween>
                  </SpaceBetween>
                </div>
              ))}
              <Button formAction="none" iconName="add-plus" variant="inline-link" onClick={() => push({ os: [], method: '', command: '' })}>
                Add Command
              </Button>
            </SpaceBetween>
          )}
        </FieldArray>
      </FormField>

      {/* Features — "What It Does" cards */}
      <FormField label="Features" description="Highlight cards shown on the MCP detail page (icon + title + short description).">
        <FieldArray name="applicationDetails.mcpConfig.features">
          {({ push, remove }) => (
            <SpaceBetween size="m">
              {(formik.values.applicationDetails?.mcpConfig?.features || []).map((_: unknown, index: number) => (
                <SpaceBetween key={index} size="xs" direction="horizontal" alignItems="end">
                  <FormField label="Icon">
                    <Select
                      selectedOption={
                        getIn(formik.values, `applicationDetails.mcpConfig.features.${index}.icon`)
                          ? FEATURE_ICON_OPTIONS.find(
                              (o) => o.value === getIn(formik.values, `applicationDetails.mcpConfig.features.${index}.icon`)
                            ) || null
                          : null
                      }
                      onChange={({ detail }) =>
                        formik.setFieldValue(`applicationDetails.mcpConfig.features.${index}.icon`, detail.selectedOption.value)
                      }
                      options={FEATURE_ICON_OPTIONS}
                      placeholder="Icon"
                      filteringType="auto"
                    />
                  </FormField>
                  <FormField label="Title">
                    <Input
                      value={getIn(formik.values, `applicationDetails.mcpConfig.features.${index}.title`) || ''}
                      onChange={({ detail }) => formik.setFieldValue(`applicationDetails.mcpConfig.features.${index}.title`, detail.value)}
                      placeholder="Query Cubes"
                    />
                  </FormField>
                  <FormField label="Description" stretch>
                    <Input
                      value={getIn(formik.values, `applicationDetails.mcpConfig.features.${index}.description`) || ''}
                      onChange={({ detail }) => formik.setFieldValue(`applicationDetails.mcpConfig.features.${index}.description`, detail.value)}
                      placeholder="Browse servers, explore cubes, and run MDX queries."
                    />
                  </FormField>
                  <Button formAction="none" iconName="remove" variant="icon" onClick={() => remove(index)} ariaLabel="Remove feature" />
                </SpaceBetween>
              ))}
              <Button formAction="none" iconName="add-plus" variant="inline-link" onClick={() => push({ icon: '', title: '', description: '' })}>
                Add Feature
              </Button>
            </SpaceBetween>
          )}
        </FieldArray>
      </FormField>

      {/* Tool Groups */}
      <FormField label="Skills" description="Group skills by category. Each group becomes a section on the MCP detail page.">
        <FieldArray name="applicationDetails.mcpConfig.toolGroups">
          {({ push: pushGroup, remove: removeGroup }) => (
            <SpaceBetween size="l">
              {(formik.values.applicationDetails?.mcpConfig?.toolGroups || []).map(
                (group: { category: string; tools: { name: string; description: string }[] }, groupIndex: number) => (
                  <div
                    key={groupIndex}
                    style={{ padding: '12px', border: '1px solid var(--color-border-divider-default, #e9ebed)', borderRadius: 8 }}
                  >
                    <SpaceBetween size="s">
                      <SpaceBetween size="xs" direction="horizontal" alignItems="end">
                        <FormField label="Category Name">
                          <Input
                            value={group.category}
                            onChange={({ detail }) =>
                              formik.setFieldValue(`applicationDetails.mcpConfig.toolGroups.${groupIndex}.category`, detail.value)
                            }
                            placeholder="Server Discovery"
                          />
                        </FormField>
                        <Button
                          formAction="none"
                          iconName="remove"
                          variant="icon"
                          onClick={() => removeGroup(groupIndex)}
                          ariaLabel="Remove category"
                        />
                      </SpaceBetween>

                      <FieldArray name={`applicationDetails.mcpConfig.toolGroups.${groupIndex}.tools`}>
                        {({ push: pushTool, remove: removeTool }) => (
                          <SpaceBetween size="xs">
                            {group.tools.map((_: unknown, toolIndex: number) => (
                              <SpaceBetween key={toolIndex} size="xs" direction="horizontal" alignItems="end">
                                <FormField label="Name">
                                  <Input
                                    value={
                                      getIn(formik.values, `applicationDetails.mcpConfig.toolGroups.${groupIndex}.tools.${toolIndex}.name`) || ''
                                    }
                                    onChange={({ detail }) =>
                                      formik.setFieldValue(
                                        `applicationDetails.mcpConfig.toolGroups.${groupIndex}.tools.${toolIndex}.name`,
                                        detail.value
                                      )
                                    }
                                    placeholder="list_cubes"
                                  />
                                </FormField>
                                <FormField label="Description" stretch>
                                  <Input
                                    value={
                                      getIn(formik.values, `applicationDetails.mcpConfig.toolGroups.${groupIndex}.tools.${toolIndex}.description`) ||
                                      ''
                                    }
                                    onChange={({ detail }) =>
                                      formik.setFieldValue(
                                        `applicationDetails.mcpConfig.toolGroups.${groupIndex}.tools.${toolIndex}.description`,
                                        detail.value
                                      )
                                    }
                                    placeholder="List all cubes in a server."
                                  />
                                </FormField>
                                <Button
                                  formAction="none"
                                  iconName="remove"
                                  variant="icon"
                                  onClick={() => removeTool(toolIndex)}
                                  ariaLabel="Remove tool"
                                />
                              </SpaceBetween>
                            ))}
                            <Button
                              formAction="none"
                              iconName="add-plus"
                              variant="inline-link"
                              onClick={() => pushTool({ name: '', description: '' })}
                            >
                              Add Skill
                            </Button>
                          </SpaceBetween>
                        )}
                      </FieldArray>
                    </SpaceBetween>
                  </div>
                )
              )}
              <Button
                formAction="none"
                iconName="add-plus"
                variant="inline-link"
                onClick={() => pushGroup({ category: '', tools: [{ name: '', description: '' }] })}
              >
                Add Category
              </Button>
            </SpaceBetween>
          )}
        </FieldArray>
      </FormField>

      {/* Resources */}
      <FormField label="Resources" description="Slack channels, wikis, and docs for support.">
        <FieldArray name="applicationDetails.mcpConfig.resources">
          {({ push, remove }) => (
            <SpaceBetween size="m">
              {(formik.values.applicationDetails?.mcpConfig?.resources || []).map((_: unknown, index: number) => (
                <div key={index} style={{ padding: '12px', border: '1px solid var(--color-border-divider-default, #e9ebed)', borderRadius: 8 }}>
                  <SpaceBetween size="s">
                    <SpaceBetween size="xs" direction="horizontal" alignItems="end">
                      <FormField label="Icon">
                        <Select
                          selectedOption={
                            getIn(formik.values, `applicationDetails.mcpConfig.resources.${index}.type`)
                              ? RESOURCE_ICON_OPTIONS.find(
                                  (o) => o.value === getIn(formik.values, `applicationDetails.mcpConfig.resources.${index}.type`)
                                ) || null
                              : null
                          }
                          onChange={({ detail }) =>
                            formik.setFieldValue(`applicationDetails.mcpConfig.resources.${index}.type`, detail.selectedOption.value)
                          }
                          options={RESOURCE_ICON_OPTIONS}
                          placeholder="Icon"
                        />
                      </FormField>
                      <FormField label="Label">
                        <Input
                          value={getIn(formik.values, `applicationDetails.mcpConfig.resources.${index}.label`) || ''}
                          onChange={({ detail }) => formik.setFieldValue(`applicationDetails.mcpConfig.resources.${index}.label`, detail.value)}
                          placeholder="#das-prophecy-team"
                        />
                      </FormField>
                      <Button formAction="none" iconName="remove" variant="icon" onClick={() => remove(index)} ariaLabel="Remove resource" />
                    </SpaceBetween>
                    <FormField label="URL">
                      <Input
                        value={getIn(formik.values, `applicationDetails.mcpConfig.resources.${index}.url`) || ''}
                        onChange={({ detail }) => formik.setFieldValue(`applicationDetails.mcpConfig.resources.${index}.url`, detail.value)}
                        placeholder="https://amzn-aws.slack.com/archives/C0XXXXXXX"
                      />
                    </FormField>
                  </SpaceBetween>
                </div>
              ))}
              <Button formAction="none" iconName="add-plus" variant="inline-link" onClick={() => push({ label: '', url: '', type: '' })}>
                Add Resource
              </Button>
            </SpaceBetween>
          )}
        </FieldArray>
      </FormField>

      {/* Contacts */}
      <MultiEmployeeLookup
        label="Contacts"
        description="Team members responsible for this MCP server. Search by name or alias."
        placeholder="Search for a team member"
        value={contactEmployees}
        onChange={handleContactsChange}
      />
    </SpaceBetween>
  );
};

export default McpConfigSection;
